import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  AsanaIntegrationConfig,
  AsanaProject,
  AsanaWorkspace
} from '@dadohr/shared/domainTypes/integrations/asana';
import { CF, FS } from '@dadohr/shared/versions';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../services/firebase';
import {
  cachedCallFirebaseFunction,
  callFirebaseFunction,
  useFirebaseFunction
} from '../../../../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../../services/firecache/documentListener';
import { ONE_MINUTE, now } from '../../../../../../services/time';

export const toAsanaConfigDoc = generateToDocFn<AsanaIntegrationConfig>();

const COLLECTION = FS.asanaIntegrationConfig;
const collection = () => store().collection(COLLECTION);

export const asanaConfigStore = createDocumentListenerGetter(
  (orgId) => collection().doc(orgId),
  toAsanaConfigDoc
);

export const useAsanaConfig = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useDocumentListener(asanaConfigStore(orgId)),
    (d) => {
      if (!d) {
        const n = now();
        const newConfig: Doc<AsanaIntegrationConfig> = {
          id: orgId,
          collection: COLLECTION,
          data: {
            orgId,
            active: false,
            createdAt: n,
            createdBy: userId,
            editedAt: n,
            editedBy: userId,

            accessToken: '',
            refreshToken: '',
            asanaUser: {
              id: '',
              email: '',
              name: ''
            },
            expireDate: 0
          }
        };
        return newConfig;
      }
      return d;
    }
  );
};

export const removeAsanaConfig = (orgId: string) => {
  return collection().doc(orgId).delete();
};

export const useAsanaWorkspaces = (orgId: string) => {
  return useFirebaseFunction<AsanaWorkspace[]>(CF.asana.getResources, {
    type: 'WORKSPACES',
    orgId
  });
};

export const getAsanaWorkspaces = (orgId: string) => {
  return callFirebaseFunction<AsanaWorkspace[]>(CF.asana.getResources, {
    type: 'WORKSPACES',
    orgId
  });
};

export const useAsanaProjectsByWorkspaceId = (
  orgId: string,
  workspaceId: string
) => {
  return cachedCallFirebaseFunction<AsanaProject[]>(
    CF.asana.getResources,
    {
      type: 'PROJECTS',
      orgId,
      workspaceId
    },
    ONE_MINUTE * 30
  );
};

export const getAsanaProjectsByWorkspaceId = (
  orgId: string,
  workspaceId: string
) => {
  return cachedCallFirebaseFunction<AsanaProject[]>(
    CF.asana.getResources,
    {
      type: 'PROJECTS',
      orgId,
      workspaceId
    },
    ONE_MINUTE * 30
  );
};

export const ASANA_COLOR_CODE_TO_HEX: { [key: string]: string } = {
  'dark-red': '#E53B52',
  'dark-orange': '#F96237',
  'light-orange': '#FA9A27',
  'dark-brown': '#ECC42C',
  'light-green': '#A5CF3F',
  'dark-green': '#66D273',
  'light-teal': '#42C4AB',
  'dark-teal': '#32A9E8',
  'light-blue': '#4886DD',
  'dark-purple': '#7B6FED',
  'light-purple': '#A962E0',
  'light-pink': '#E163E1',
  'dark-pink': '#E84F9C',
  'light-red': '#FA91AE',
  'light-warm-gray': '#8DA3A6'
};
